import { motion, useMotionValue, useTransform, AnimatePresence } from "framer-motion";
import { useState, useRef } from "react";
import { API_URL } from "../config";

function FormHome() {
    const x = useMotionValue(0);
    const y = useMotionValue(0);
    const translateX = useTransform(y, [-400, 400], [45, -45]);
    const translateY = useTransform(x, [-400, 400], [-45, 45]);

    const [formData, setFormData] = useState({ email: '', subject: '', message: '' });
    const [file, setFile] = useState(null);
    const [fileName, setFileName] = useState('');
    const [filePreview, setFilePreview] = useState(null);
    const [isDragging, setIsDragging] = useState(false);
    const [status, setStatus] = useState(null);
    const [errorMsg, setErrorMsg] = useState('');
    const fileInputRef = useRef(null);

    function handleMouse(event) {
        const rect = event.currentTarget.getBoundingClientRect();
        x.set(event.clientX - rect.left);
        y.set(event.clientY - rect.top);
    }

    function handleChange(e) {
        setFormData({ ...formData, [e.target.name]: e.target.value });
    }

    function processFile(f) {
        if (f.size > 10 * 1024 * 1024) {
            setErrorMsg('Le fichier ne doit pas dépasser 10 Mo');
            setStatus('error');
            setTimeout(() => setStatus(null), 4000);
            return;
        }
        setFile(f);
        setFileName(f.name);

        if (f.type.startsWith('image/')) {
            const reader = new FileReader();
            reader.onload = (ev) => setFilePreview(ev.target.result);
            reader.readAsDataURL(f);
        } else {
            setFilePreview(null);
        }
    }

    function handleFileChange(e) {
        const f = e.target.files[0];
        if (f) processFile(f);
    }

    function handleDrop(e) {
        e.preventDefault();
        setIsDragging(false);
        const f = e.dataTransfer.files[0];
        if (f) processFile(f);
    }

    function handleDragOver(e) {
        e.preventDefault();
        setIsDragging(true);
    }

    function handleDragLeave() {
        setIsDragging(false);
    }

    function removeFile() {
        setFile(null);
        setFileName('');
        setFilePreview(null);
        if (fileInputRef.current) fileInputRef.current.value = '';
    }

    function formatFileSize(bytes) {
        if (bytes < 1024) return bytes + ' o';
        if (bytes < 1024 * 1024) return (bytes / 1024).toFixed(1) + ' Ko';
        return (bytes / (1024 * 1024)).toFixed(1) + ' Mo';
    }

    function getFileIcon(name) {
        const ext = name.split('.').pop().toLowerCase();
        if (['jpg', 'jpeg', 'png', 'gif', 'webp', 'svg'].includes(ext)) return 'fa-image';
        if (['pdf'].includes(ext)) return 'fa-file-pdf';
        if (['doc', 'docx'].includes(ext)) return 'fa-file-word';
        if (['xls', 'xlsx'].includes(ext)) return 'fa-file-excel';
        if (['mp4', 'mov', 'avi'].includes(ext)) return 'fa-file-video';
        if (['zip', 'rar'].includes(ext)) return 'fa-file-zipper';
        return 'fa-file';
    }

    async function handleSubmit(e) {
        e.preventDefault();

        if (!formData.email || !formData.subject || !formData.message) {
            setErrorMsg('Veuillez remplir tous les champs obligatoires');
            setStatus('error');
            setTimeout(() => setStatus(null), 4000);
            return;
        }

        setStatus('sending');

        try {
            const data = new FormData();
            data.append('email', formData.email);
            data.append('subject', formData.subject);
            data.append('message', formData.message);
            if (file) data.append('attachment', file);

            const res = await fetch(`${API_URL}/contact`, {
                method: 'POST',
                body: data,
            });

            const result = await res.json();

            if (result.success) {
                setStatus('success');
                setFormData({ email: '', subject: '', message: '' });
                removeFile();
                setTimeout(() => setStatus(null), 5000);
            } else {
                setErrorMsg(result.error || 'Une erreur est survenue');
                setStatus('error');
                setTimeout(() => setStatus(null), 4000);
            }
        } catch (err) {
            setErrorMsg('Impossible d\'envoyer le message. Réessayez plus tard.');
            setStatus('error');
            setTimeout(() => setStatus(null), 4000);
        }
    }

    return (
        <motion.div
            id="contact"
            className="formhome min-[320px]:p-6 xl:p-16 grid min-[320px]:grid-cols-1 xl:grid-cols-2"
            onMouseMove={handleMouse}
        >

            <div className="formhome-title min-[320px]:w-full xl:w-3/5">
                <p className="formhome-left-content min-[320px]:text-f-40 xl:text-title-xl">
                    Une idée ? <br />
                    Un projet ? <br />
                    Un message ? <br />
                </p>
                <motion.div
                    style={{
                        width: 200,
                        height: 200,
                        borderRadius: 200,
                        backgroundColor: "#fff",
                        position: "absolute",
                        top: -20,
                        left: 200,
                        zIndex: -1,
                        translateX: translateX,
                        translateY: translateY,
                    }}
                />
                <p className="formhome-text">Contactez-nous et discutons de votre prochain projet.</p>
            </div>

            <div className="formhome-form">
                <form onSubmit={handleSubmit}>
                    <div className="form-field">
                        <input
                            type="email"
                            name="email"
                            required
                            placeholder="Email*"
                            value={formData.email}
                            onChange={handleChange}
                            disabled={status === 'sending'}
                        />
                    </div>

                    <div className="form-field">
                        <input
                            type="text"
                            name="subject"
                            required
                            placeholder="Objet*"
                            value={formData.subject}
                            onChange={handleChange}
                            disabled={status === 'sending'}
                        />
                    </div>

                    <div className="form-field">
                        <textarea
                            rows="12"
                            name="message"
                            required
                            placeholder="Bonjour, je vous contacte car..."
                            value={formData.message}
                            onChange={handleChange}
                            disabled={status === 'sending'}
                        />
                    </div>

                    {!fileName ? (
                        <div
                            className={`file-dropzone ${isDragging ? 'dragging' : ''}`}
                            onClick={() => fileInputRef.current?.click()}
                            onDrop={handleDrop}
                            onDragOver={handleDragOver}
                            onDragLeave={handleDragLeave}
                        >
                            <input
                                type="file"
                                ref={fileInputRef}
                                onChange={handleFileChange}
                                disabled={status === 'sending'}
                            />
                            <div className="file-dropzone-icon">
                                <i className="fa-solid fa-cloud-arrow-up"></i>
                            </div>
                            <p className="file-dropzone-text">
                                Glissez un fichier ici ou <span>parcourir</span>
                            </p>
                            <p className="file-dropzone-hint">
                                Max 10 Mo
                            </p>
                        </div>
                    ) : (
                        <div className="file-selected">
                            <div className={`file-selected-icon ${filePreview ? 'has-preview' : 'no-preview'}`}>
                                {filePreview ? (
                                    <img src={filePreview} alt="" />
                                ) : (
                                    <i className={`fa-solid ${getFileIcon(fileName)}`}></i>
                                )}
                            </div>
                            <div className="file-selected-info">
                                <div className="file-selected-name">{fileName}</div>
                                <div className="file-selected-size">{file && formatFileSize(file.size)}</div>
                            </div>
                            <button type="button" className="file-selected-remove" onClick={removeFile}>
                                ✕
                            </button>
                        </div>
                    )}

                    <button
                        className="btn-blackarrow"
                        type="submit"
                        disabled={status === 'sending'}
                    >
                        {status === 'sending' ? 'Envoi en cours...' : 'Envoyer'}
                    </button>
                </form>
            </div>

            <AnimatePresence>
                {status === 'sending' && (
                    <motion.div
                        className="form-notification sending"
                        initial={{ opacity: 0, y: 20, scale: 0.95 }}
                        animate={{ opacity: 1, y: 0, scale: 1 }}
                        exit={{ opacity: 0, y: 20, scale: 0.95 }}
                        transition={{ duration: 0.3 }}
                    >
                        <div className="form-spinner" />
                        <span>Envoi en cours...</span>
                    </motion.div>
                )}

                {status === 'success' && (
                    <motion.div
                        className="form-notification success"
                        initial={{ opacity: 0, y: 20, scale: 0.95 }}
                        animate={{ opacity: 1, y: 0, scale: 1 }}
                        exit={{ opacity: 0, y: 20, scale: 0.95 }}
                        transition={{ duration: 0.3 }}
                    >
                        <span className="notif-icon">✓</span>
                        <span>Message envoyé avec succès !</span>
                    </motion.div>
                )}

                {status === 'error' && (
                    <motion.div
                        className="form-notification error"
                        initial={{ opacity: 0, y: 20, scale: 0.95 }}
                        animate={{ opacity: 1, y: 0, scale: 1 }}
                        exit={{ opacity: 0, y: 20, scale: 0.95 }}
                        transition={{ duration: 0.3 }}
                    >
                        <span className="notif-icon">✕</span>
                        <span>{errorMsg}</span>
                    </motion.div>
                )}
            </AnimatePresence>
        </motion.div>
    );
}

export default FormHome;